/* eslint-disable no-param-reassign */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle, Box, Button, Divider, Grid, Link, Paper, Stack, Tab, Tabs, Typography, alpha, Tooltip } from '@mui/material';
import HelpCenter from '@mui/icons-material/HelpCenter';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import dayjs from 'dayjs';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import { FormErrorSummary } from 'src/shared/components/form/formErrorSummary';
import { toast } from 'react-toastify';
import { useNavigate, useParams, useBlocker } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { SelectionForm } from 'src/modules/groupMessageEmail/components/selectionForm';
import { Selection } from 'src/modules/groupMessageEmail/models/selection';
import { updateGroupMessage, getGroupMessageById, getSelectionById, cancelGroupMessageSchedule } from 'src/shared/services/groupMessageService';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { ControlledFileDropZone } from 'src/shared/components/form/fileDropZone';
import { DocumentFile } from 'src/shared/models/documentFile';
import { LoadingButton } from '@mui/lab';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { removeBase64Images } from 'src/shared/functions/removeBase64Images';
import { isDesignModeCompatible } from 'src/modules/groupMessageEmail/builder/parse';

import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { ContentSection } from 'src/shared/components/organisms/contentSection';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { DashboardCustomize, Drafts, Edit, Pageview, Save } from '@mui/icons-material';
import { GroupMessageRequestPut } from '../models/groupMessageRequestPut';
import { FixedSelection } from '../models/fixedSelection';
import { RemovedImagesDialog } from '../components/removedImagesDialog';
import { HandleGroupMessageMenu } from '../components/handleGroupMessageMenu';
import { GroupMessageResponse } from '../models/groupMessageResponse';
import { GroupMessageEmailStatusMessage } from '../components/groupMessageEmailStatusMessage';
import { GroupMessageIsDeletedStatusMessage } from '../components/groupMessageIsDeletedStatusMessage';
import { GroupMessageEmailTemplateView } from '../components/groupMessageEmailTemplateView';
import { EmailBuilderModal, EmailBuilderMode } from '../components/emailBuilderModal';
import { SendSplitButton } from '../components/sendSplitButton';
import { SendMessageModal } from '../components/sendMessageModal';
import { SendMessageSummary } from '../components/sendMessageSummary';
import { GroupMessageEmailPreviewModal } from '../components/groupMessageEmailPreviewModal';
import { attachmentsExceedMax, shouldSendAttachmentsAsLinks, sumDocumentFileSizes } from '../functions/attachmentSize';

const GroupMessageEmailEditPage = () => {
  const { t } = useTranslation(['groupMessage', 'common']);
  const params = useParams();
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState<boolean>(true);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [fixedSelection, setFixedSelection] = useState<FixedSelection | null>(null);
  const [documentFiles, setDocumentFiles] = useState<DocumentFile[] | undefined>();
  const [documentIds, setDocumentIds] = useState<string[]>([]);
  const [sendMode, setSendMode] = useState<boolean>(false);
  const [removedImages, setRemovedImages] = useState<string[]>([]);
  const [dataToSave, setDataToSave] = useState<GroupMessageRequestPut>();
  const [groupMessageResponse, setGroupMessageResponse] = useState<GroupMessageResponse>();
  // State för designändringarna: builder-modal, tab-växling (Redigera/Förhandsgranska), send-modal och schemaläggningsläge.
  const [builderMode, setBuilderMode] = useState<EmailBuilderMode | null>(null);
  const [previewTab, setPreviewTab] = useState<'preview' | 'edit'>('edit');
  const [sendModalOpen, setSendModalOpen] = useState<boolean>(false);
  const [scheduleMode, setScheduleMode] = useState<'now' | 'schedule'>('now');
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const [showCancelScheduleDialog, setShowCancelScheduleDialog] = useState<boolean>(false);

  const requiredMessage = t('common:validationMessages.required');

  const GroupMessageRequestPutSchema: Schema<GroupMessageRequestPut> = yup.object().shape({
    circular_id: yup.number().required(requiredMessage),
    product_id: yup.number().optional(),
    selection_id: yup.number().required(requiredMessage),
    circularName: yup.string().optional().nullable(),
    selectionName: yup.string().optional().nullable(),
    recipients: yup
      .string()
      .trim()
      .matches(
        /^(([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5}){1,25})+([;.](([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5}){1,25})+)*$/,
        {
          message: t('common:validationMessages.multipleEmail'),
          excludeEmptyString: true,
        },
      ),
    subject: yup
      .string()
      .required(requiredMessage)
      .max(70, t('common:validationMessages.maxLength', { count: 70 })),
    body: yup.string().required(requiredMessage),
    plainText: yup.string().optional().nullable(),
    isHTML: yup.boolean().required(),
    useLinkCounter: yup.boolean().optional(),
    sendDate: yup.string().optional().nullable(),
    sender: yup.string().email(t('common:validationMessages.email')).required(requiredMessage),
    documentFiles: yup.mixed().optional(),
    documentIds: yup.string().optional().nullable(),
    localAss_id: yup.number().optional(),
    countyAss_id: yup.number().optional(),
    senderTitle: yup.string().required(),
    selectionType: yup.number().optional(),
    speedFormValues: yup.string().optional(),
    speedFormText: yup.string().optional(),
    files: yup.mixed<File[]>().optional().nullable().notRequired(),
    yvMultiCustName: yup.string().optional(),
    created: yup.string().optional(),
    send: yup.boolean().optional(),
  });

  const formMethods = useForm<GroupMessageRequestPut>({
    resolver: yupResolver(GroupMessageRequestPutSchema),
    mode: 'all',
    defaultValues: {
      subject: '',
      sender: '',
      senderTitle: '',
      documentIds: '',
      body: '',
      selection_id: 0,
      send: false,
      speedFormText: '',
      speedFormValues: '',
    },
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
    trigger,
    getValues,
    reset,
    setValue,
    watch,
  } = formMethods;

  // Save-flödet sätter denna till true precis innan navigate så vi inte triggar leave-dialogen.
  const finalizingSaveRef = useRef(false);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (finalizingSaveRef.current) return false;
    if (isSubmitting) return false;
    if (!isDirty) return false; // inga osparade ändringar → låt navigationen ske
    return currentLocation.pathname !== nextLocation.pathname;
  });

  // Tab-close / hård refresh
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!isSubmitting && !finalizingSaveRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isSubmitting]);

  const fetchData = useCallback(async () => {
    try {
      const result = await getGroupMessageById(params.id);
      setLoading(false);
      ConsoleHelper.log('fetch', result.data);

      result.data.selection_id = result.data.selection_id ?? '';
      result.data.recipients = result.data.recipients ?? '';
      reset(result.data);
      setGroupMessageResponse(result.data);
      setDocumentFiles(result.data.documentFiles);
      setDocumentIds(result.data.documentIds?.split(',') || []);
      if (result.data.speedFormValues) {
        setFixedSelection({
          name: result.data.speedFormText,
          value: result.data.speedFormValues,
        } as FixedSelection);
      }
      if (result.data.selection_id !== 0) {
        const sel = await getSelectionById(result.data.selection_id);
        setSelection(sel.data as Selection);
      }
    } catch (e) {
      ConsoleHelper.error('getSelectionById error:', e);
    }
  }, [params.id, reset]);

  useEffect(() => {
    if (isMounted() && params.id) {
      fetchData();
    }
  }, [fetchData, isMounted, params.id]);

  // TinyMCE och vissa andra controllers fire:ar onChange under init, vilket markerar formuläret som dirty även
  // utan att användaren gjort något. Vänta en kort stund efter att utskicket laddats och re-baselina formuläret
  // så isDirty blir false tills användaren faktiskt skriver något.
  useEffect(() => {
    if (!groupMessageResponse) return undefined;
    const timer = setTimeout(() => {
      reset(getValues());
    }, 500);
    return () => clearTimeout(timer);
  }, [groupMessageResponse, reset, getValues]);

  const goToOverview = useCallback(() => {
    navigate(RoutesEnum.GroupMessageEmailOverview);
  }, [navigate]);

  const postData = useCallback(
    async (data) => {
      ConsoleHelper.log('postData', data);
      try {
        // Schemaläggning: lokal tid i ISO-format, annars nollas sendDate.
        if (data.send && scheduleMode === 'schedule' && data.sendDate) {
          data.sendDate = dayjs(data.sendDate).format('YYYY-MM-DDTHH:mm:ss');
        } else if (!data.send) {
          data.sendDate = null;
        }
        const res = await updateGroupMessage(data);
        if (isMounted()) {
          ConsoleHelper.log('res updateGroupMessage', res);
          if (data.send && scheduleMode === 'schedule') {
            toast.success(t('groupMessage:create.scheduleSuccess', { when: dayjs(data.sendDate).format('YYYY-MM-DD HH:mm') }) as string);
          } else if (data.send) {
            toast.success(t('groupMessage:edit.sendSuccess') as string);
          } else {
            toast.success(t('groupMessage:edit.saveSuccess') as string);
          }
          setSendModalOpen(false);
          finalizingSaveRef.current = true;
          // Vid Skicka/Schemalägg → tillbaka till översikten. Vid bara Spara → stanna kvar.
          if (data.send) {
            goToOverview();
          } else {
            // Markera nuvarande form-värden som de nya defaults — gör isDirty=false så blocker släpper navigation.
            // Använd getValues() istället för det muterade data-objektet (som innehåller excludeMemberIds m.fl. som inte är form-fält).
            reset(getValues());
            finalizingSaveRef.current = false;
          }
        }
      } catch (e) {
        if (data.send) toast.warning(t('groupMessage:edit.sendError') as string);
        else toast.warning(t('groupMessage:edit.saveError') as string);
        ConsoleHelper.log('saveFormDataError:', e);
      }
    },
    [goToOverview, isMounted, scheduleMode, t],
  );

  const saveFormData = useCallback(
    async (data: GroupMessageRequestPut) => {
      // Blockera om bilagorna (nya + redan sparade) överstiger det hårda taket – hela requesten måste rymmas i serverns gräns.
      if (attachmentsExceedMax(data.files, documentFiles)) {
        setSendModalOpen(false);
        toast.warning(t('groupMessage:create.attachmentsTooLarge') as string);
        return;
      }
      data.includeRecipientsThatHasUnsubscribedFromEmail = false;
      data.speedFormText = data.speedFormText ?? '.';
      data.speedFormValues = data.speedFormValues ?? '.';
      data.send = sendMode;
      data.documentIds = documentIds.join(',');
      // Skicka bilagor som länkar när totala storleken (nya filer + redan sparade bilagor) överstiger 10 MB (work item 6051).
      data.attachmentsAsLinks = shouldSendAttachmentsAsLinks(data.files, documentFiles);
      const { modifiedContent, strippedImages } = removeBase64Images(data.body);
      // Spara arrayen med bilder i statet
      setRemovedImages(strippedImages);
      if (strippedImages.length > 0) {
        data.body = modifiedContent;
        ConsoleHelper.log('modifiedContent', modifiedContent);
        setValue('body', modifiedContent);
        setDataToSave(data);
      } else {
        await postData(data);
      }
    },
    [documentIds, documentFiles, postData, sendMode, setValue, t],
  );

  const handleConfirmImageRemoval = () => {
    postData(dataToSave);
  };

  const onSubmit = async (data: GroupMessageRequestPut) => {
    await trigger();
    if (Object.keys(errors).length === 0) {
      try {
        // Mappa exkluderade medlemmar till excludeMemberIds (backend läser inte object-arrayen).
        const { excludedMemberFromSelection, ...rest } = data as GroupMessageRequestPut & {
          // eslint-disable-next-line camelcase
          excludedMemberFromSelection?: { member_id?: number }[];
        };
        await saveFormData({
          ...rest,
          // eslint-disable-next-line camelcase
          excludeMemberIds: excludedMemberFromSelection?.map((m) => m.member_id).filter((id): id is number => id !== undefined) ?? [],
        });
      } catch (e) {
        toast.error('Misslyckades');
      }
    }
  };

  const onDocumentFileRemove = (fileId: string) => {
    const updatedDocumentIds = documentIds.filter((item) => item.toString() !== fileId);
    setDocumentIds(updatedDocumentIds);
    setDocumentFiles(documentFiles?.filter((item) => item.document_id.toString() !== fileId));
    // Håll formulärets documentIds-fält i synk, annars läser Skicka testmail (GroupMessageEmailPreviewModal)
    // fortfarande de gamla id:na via getValues('documentIds') trots att bilagan tagits bort i UI:t.
    setValue('documentIds', updatedDocumentIds.join(','), { shouldDirty: true });
  };

  const returnToOverview = () => {
    navigate(RoutesEnum.GroupMessageEmailOverview);
  };

  const handleCopy = () => {
    returnToOverview();
  };

  // Skickat = sendDate satt OCH ligger i förflutet. Schemalagt (sendDate framtida) räknas inte som skickat.
  // Drafts (sendDate=null) räknas inte som skickade.
  const sendDateValue = groupMessageResponse?.sendDate;
  const isScheduled = !!(sendDateValue && dayjs(sendDateValue).isAfter(dayjs()));
  const messageIsSent = !!(sendDateValue && !isScheduled);
  const isDeleted = groupMessageResponse?.deletedAt != null;
  // Schemalagda låses också — användaren måste avbryta schemaläggningen för att kunna redigera.
  const isLocked = messageIsSent || isDeleted || isScheduled;
  // Designläge är bara säkert för utskick byggda med designeditorn (har data-email-doc-markör).
  // Legacy HTML korrumperas vid round-trip.
  const bodyValue = watch('body');
  const designModeAvailable = isDesignModeCompatible(bodyValue);
  const designModeDisabled = isLocked || !designModeAvailable;
  // För design-mallar finns ingen "Redigera innehåll"-flik — då tvingar vi alltid preview.
  const effectivePreviewTab = designModeAvailable ? 'preview' : previewTab;

  const handleCancelScheduleDialogClose = async (result?: { id: string }) => {
    setShowCancelScheduleDialog(false);
    if (!result || result.id !== 'cancelSchedule' || !groupMessageResponse) return;
    try {
      await cancelGroupMessageSchedule(groupMessageResponse.circular_id);
      toast.success(t('groupMessage:schedule.cancelSuccess') as string);
      // Ladda om utskicket så sendDate nollställs och låset släpps
      fetchData();
    } catch (e: any) {
      if (e?.response?.status === 409) {
        toast.warning(t('groupMessage:schedule.cancelTooLate') as string);
      } else {
        toast.error(t('groupMessage:schedule.cancelTooLate') as string);
      }
      ConsoleHelper.log('cancelGroupMessageSchedule error', e);
    }
  };

  // Öppna send-modalen efter validering. Markerar sendMode=true så onSubmit skickar.
  const openSendModal = useCallback(
    async (mode: 'now' | 'schedule') => {
      const valid = await trigger();
      if (!valid) return;
      // Öppna inte skicka-modalen om bilagorna (nya + redan sparade) överstiger det hårda taket.
      if (attachmentsExceedMax(getValues('files'), documentFiles)) {
        toast.warning(t('groupMessage:create.attachmentsTooLarge') as string);
        return;
      }
      setScheduleMode(mode);
      setSendMode(true);
      setSendModalOpen(true);
    },
    [trigger, getValues, documentFiles, t],
  );

  // Knapparna återanvänds både i sidans header och längst ned vid formuläret
  const actionButtons = (
    <Stack direction='row' spacing={1} alignItems='center'>
      <Button
        variant='outlined'
        type='button'
        disabled={Object.keys(errors).length > 0 || isLocked}
        onClick={async () => {
          await trigger();
          if (Object.keys(errors).length === 0) setShowPreviewModal(true);
        }}
        startIcon={<Drafts aria-hidden />}
      >
        {t('groupMessage:builder.sendTestMail')}
      </Button>
      <LoadingButton
        loading={isSubmitting}
        variant='contained'
        type='submit'
        disableElevation
        onClick={() => setSendMode(false)}
        disabled={Object.keys(errors).length > 0 || isLocked}
        startIcon={<Save aria-hidden />}
      >
        {t('common:button.save')}
      </LoadingButton>
      <Divider orientation='vertical' variant='middle' flexItem />
      <SendSplitButton
        loading={isSubmitting}
        disabled={Object.keys(errors).length > 0 || isLocked || (!selection && !fixedSelection)}
        onSendNow={() => openSendModal('now')}
        onSchedule={() => openSendModal('schedule')}
      />
    </Stack>
  );

  return (
    <FormLoaderWrapper loading={loading}>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <GridWrapper fixed>
            <Grid
              item
              xs={12}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16, marginBottom: -10 }}
            >
              <MainContentHeader title={t('groupMessage:edit.header')} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Link
                  href='https://hjalp.foreningssupport.se/article/312-kommunikation-e-postutskick#Skapa-ett-nytt-e-postutskick-mWATY'
                  target='_blank'
                  rel='noopener'
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                >
                  <HelpCenter fontSize='small' />
                  {t('groupMessage:create.helpLinkText')}
                </Link>
                {params.id && (
                  <HandleGroupMessageMenu
                    disabled={false}
                    groupMessageId={params.id}
                    callback={handleCopy}
                    deletedAt={getValues('deletedAt') ?? null}
                    messageIsSent={messageIsSent}
                    refetchCallback={(val: boolean) => {
                      if (val === true) {
                        fetchData();
                      }
                    }}
                  />
                )}
                {!isDeleted && actionButtons}
              </Box>
            </Grid>
            {removedImages && removedImages.length > 0 && (
              <RemovedImagesDialog
                removedImages={removedImages}
                open
                handleClose={() => setRemovedImages([])}
                confirmImageRemoval={handleConfirmImageRemoval}
              />
            )}
            <EmailBuilderModal open={builderMode !== null} mode={builderMode ?? 'design'} onClose={() => setBuilderMode(null)} />
            <GroupMessageEmailPreviewModal
              modalOpen={showPreviewModal}
              onClose={() => setShowPreviewModal(false)}
              onSave={() => {
                setShowPreviewModal(false);
                // Test-mail-send sparar innehållet på backend via PUT /Preview. Återbaselina
                // formuläret så useBlocker inte varnar om "osparade ändringar" när användaren
                // navigerar bort efteråt.
                reset(getValues());
              }}
              existingId={groupMessageResponse?.circular_id}
            />
            <DeleteConfirmationDialog
              open={showCancelScheduleDialog}
              title={t('groupMessage:schedule.cancelTitle')}
              modaltext={t('groupMessage:schedule.cancelConfirm')}
              value='cancelSchedule'
              confirmButtonText={t('groupMessage:schedule.cancelButton')}
              onClose={handleCancelScheduleDialogClose}
            />
            <SendMessageModal
              open={sendModalOpen}
              mode={scheduleMode}
              loading={isSubmitting}
              recipientsLabel={selection?.selectionName || fixedSelection?.name || undefined}
              existingAttachmentsCount={documentFiles?.length ?? 0}
              existingAttachmentsBytes={sumDocumentFileSizes(documentFiles)}
              onClose={() => setSendModalOpen(false)}
              onConfirm={async () => {
                await handleSubmit(onSubmit)();
              }}
            />
            <DeleteConfirmationDialog
              open={blocker.state === 'blocked'}
              title={t('groupMessage:create.unsavedChangesTitle')}
              modaltext={t('groupMessage:create.unsavedChangesText')}
              value='leave'
              confirmButtonText={t('groupMessage:create.leaveAnyway')}
              onClose={(result) => {
                if (result?.id === 'leave') {
                  finalizingSaveRef.current = true;
                  blocker.proceed?.();
                } else {
                  blocker.reset?.();
                }
              }}
            />
            {messageIsSent && groupMessageResponse && (
              <GroupMessageEmailStatusMessage groupMessageEmailResponse={groupMessageResponse} callback={() => ConsoleHelper.log('test')} />
            )}
            {isDeleted && <GroupMessageIsDeletedStatusMessage deletedAt={getValues('deletedAt') || ''} deletedBy={getValues('deletedBy') || ''} />}
            {isScheduled && (
              <Grid item xs={12}>
                <Alert
                  severity='warning'
                  action={
                    <Button color='inherit' size='small' onClick={() => setShowCancelScheduleDialog(true)} sx={{ alignSelf: 'center' }}>
                      {t('groupMessage:schedule.cancelButton')}
                    </Button>
                  }
                >
                  <AlertTitle>{t('groupMessage:schedule.scheduledFor', { date: dayjs(sendDateValue).format('YYYY-MM-DD HH:mm') })}</AlertTitle>
                  {t('groupMessage:schedule.lockedHint')}
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} sx={messageIsSent || isScheduled || isDeleted ? { mt: -1.5 } : undefined}>
              <FormErrorSummary translationNamespace='groupMessage' errors={errors} />
            </Grid>
            <Grid item xs={12} sx={messageIsSent || isScheduled || isDeleted ? { mt: -1.5 } : undefined}>
              <Paper
                sx={{
                  p: 3,
                }}
              >
                <GridWrapper>
                  <Grid item xs={12} lg={4}>
                    {messageIsSent ? (
                      <ContentSection header={t('groupMessage:create.summary.title')} headerVariant='subtitle1' headerFontWeight={600}>
                        <Box sx={{ pt: 1 }}>
                          <SendMessageSummary
                            recipientsLabel={selection?.selectionName || fixedSelection?.name || undefined}
                            existingAttachmentsCount={documentFiles?.length ?? 0}
                            existingAttachmentsBytes={sumDocumentFileSizes(documentFiles)}
                          />
                        </Box>
                      </ContentSection>
                    ) : (
                      <Stack spacing={2} useFlexGap sx={{ mt: 0 }}>
                        <SelectionForm
                          disabled={isLocked}
                          selectionFromServer={selection}
                          fixedSelectionFromServer={fixedSelection}
                          onSelection={(v) => setSelection(v)}
                          onFixedSelection={(v) => setFixedSelection(v)}
                          headerVariant='subtitle1'
                          headerFontWeight={600}
                        />

                        <ControlledTextField
                          name='recipients'
                          label={t('groupMessage:formFields.recipients')}
                          helperText={t('groupMessage:formFields.recipients_helperText')}
                          disabled={isLocked}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />

                        <ContentSection header={t('groupMessage:sections.senderAndSubject')} headerVariant='subtitle1' headerFontWeight={600}>
                          <Stack spacing={2} sx={{ pt: 1 }}>
                            <ControlledTextField
                              name='subject'
                              required
                              label={t('groupMessage:formFields.subject')}
                              disabled={isLocked}
                              inputProps={{ maxLength: 70 }}
                              InputLabelProps={{
                                shrink: true,
                              }}
                            />
                            <ControlledTextField
                              name='senderTitle'
                              required
                              label={t('groupMessage:formFields.senderTitle')}
                              disabled={isLocked}
                              InputLabelProps={{
                                shrink: true,
                              }}
                            />
                            <ControlledTextField
                              name='sender'
                              required
                              label={t('groupMessage:formFields.sender')}
                              disabled={isLocked}
                              InputLabelProps={{
                                shrink: true,
                              }}
                            />
                          </Stack>
                        </ContentSection>

                        <ContentSection header={t('groupMessage:sections.attachments')} headerVariant='subtitle1' headerFontWeight={600}>
                          <ControlledFileDropZone
                            name='files'
                            multiple
                            disabled={isLocked}
                            orientation='vertical'
                            documentFiles={documentFiles}
                            onDocumentFileRemove={onDocumentFileRemove}
                          />
                        </ContentSection>
                      </Stack>
                    )}
                  </Grid>
                  <Grid item xs={12} lg={8}>
                    {messageIsSent ? (
                      <ContentSection header={t('groupMessage:builder.previewTab')} headerVariant='subtitle1' headerFontWeight={600}>
                        <Box sx={{ pt: 1 }}>
                          <Box
                            sx={{
                              width: '100%',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              bgcolor: 'background.paper',
                              overflow: 'hidden',
                              boxShadow: 1,
                            }}
                          >
                            {/* Mail-header i Gmail/Outlook-stil */}
                            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                              <Typography variant='h6' sx={{ lineHeight: 1.3, mb: 1, fontWeight: 500 }}>
                                {watch('subject') || ''}
                              </Typography>
                              <Stack direction='row' alignItems='center' spacing={1.5}>
                                <Box
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    flexShrink: 0,
                                  }}
                                >
                                  {(watch('senderTitle') || '?').charAt(0).toUpperCase()}
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography variant='body2' sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                                    {watch('senderTitle') || ''}
                                  </Typography>
                                  <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                                    {watch('sender') || ''}
                                  </Typography>
                                </Box>
                              </Stack>
                            </Box>
                            <GroupMessageEmailTemplateView body={watch('body') || ''} height='40rem' />
                          </Box>
                        </Box>
                      </ContentSection>
                    ) : (
                      <Stack spacing={2} useFlexGap sx={{ mt: 0 }}>
                        <ContentSection>
                          {/* Tab-rad: Redigera innehåll / Förhandsgranska till vänster, Designläge till höger */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              borderBottom: 1,
                              borderColor: 'divider',
                              px: 1,
                            }}
                          >
                            <Tabs
                              value={designModeAvailable ? 'preview' : previewTab}
                              onChange={(_, value: 'preview' | 'edit') => setPreviewTab(value)}
                              sx={{ minHeight: 'auto' }}
                            >
                              {!designModeAvailable && (
                                <Tab
                                  value='edit'
                                  label={t('groupMessage:sections.editContent')}
                                  icon={<Edit fontSize='small' />}
                                  iconPosition='start'
                                  sx={{ minHeight: 36, py: 0 }}
                                />
                              )}
                              <Tab
                                value='preview'
                                label={t('groupMessage:builder.previewTab')}
                                icon={<Pageview fontSize='small' />}
                                iconPosition='start'
                                sx={{ minHeight: 36, py: 0 }}
                              />
                            </Tabs>
                            <Tooltip title={!isLocked && !designModeAvailable ? (t('groupMessage:builder.designModeLockedTooltip') as string) : ''}>
                              <span>
                                <Button
                                  size='small'
                                  variant='outlined'
                                  startIcon={<DashboardCustomize fontSize='small' />}
                                  onClick={() => setBuilderMode('design')}
                                  disabled={designModeDisabled}
                                >
                                  {t('groupMessage:builder.designMode')}
                                </Button>
                              </span>
                            </Tooltip>
                          </Box>

                          {effectivePreviewTab === 'preview' && (
                            <Box sx={{ pt: 2 }}>
                              <Box
                                sx={{
                                  position: 'relative',
                                  width: '100%',
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.paper',
                                  overflow: 'hidden',
                                  boxShadow: 1,
                                  transition: 'box-shadow 120ms ease',
                                  '&:hover': {
                                    boxShadow: 3,
                                    '& .builder-overlay': {
                                      opacity: 1,
                                      pointerEvents: 'auto',
                                    },
                                  },
                                }}
                              >
                                {/* Mail-header i Gmail/Outlook-stil */}
                                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                                  <Typography
                                    variant='h6'
                                    sx={{
                                      lineHeight: 1.3,
                                      mb: 1,
                                      fontWeight: 500,
                                      color: watch('subject') ? 'text.primary' : 'text.disabled',
                                    }}
                                  >
                                    {watch('subject') || t('groupMessage:preview.subjectPlaceholder')}
                                  </Typography>
                                  <Stack direction='row' alignItems='center' spacing={1.5}>
                                    <Box
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        bgcolor: 'primary.main',
                                        color: 'primary.contrastText',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                        flexShrink: 0,
                                      }}
                                    >
                                      {(watch('senderTitle') || '?').charAt(0).toUpperCase()}
                                    </Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                      <Typography
                                        variant='body2'
                                        sx={{
                                          fontWeight: 500,
                                          color: watch('senderTitle') ? 'text.primary' : 'text.disabled',
                                          lineHeight: 1.3,
                                        }}
                                      >
                                        {watch('senderTitle') || t('groupMessage:preview.senderTitlePlaceholder')}
                                      </Typography>
                                      <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                                        {watch('sender') || ''}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                </Box>

                                <GroupMessageEmailTemplateView body={watch('body') || ''} height='40rem' />

                                {/* Hover-overlay med centrerade edit-knappar */}
                                <Box
                                  className='builder-overlay'
                                  sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2,
                                    bgcolor: (theme) => alpha(theme.palette.common.black, 0.45),
                                    opacity: 0,
                                    pointerEvents: 'none',
                                    transition: 'opacity 150ms ease',
                                  }}
                                >
                                  <Stack direction='row' spacing={2}>
                                    {!designModeAvailable && (
                                      <Button
                                        variant='contained'
                                        size='large'
                                        startIcon={<Edit />}
                                        disableElevation
                                        onClick={() => setPreviewTab('edit')}
                                        sx={{ boxShadow: 4 }}
                                      >
                                        {t('groupMessage:sections.editContent')}
                                      </Button>
                                    )}
                                    <Tooltip
                                      title={!isLocked && !designModeAvailable ? (t('groupMessage:builder.designModeLockedTooltip') as string) : ''}
                                    >
                                      <span>
                                        <Button
                                          variant='contained'
                                          size='large'
                                          color='inherit'
                                          startIcon={<DashboardCustomize />}
                                          onClick={() => setBuilderMode('design')}
                                          disabled={designModeDisabled}
                                          sx={{
                                            bgcolor: 'background.paper',
                                            color: 'text.primary',
                                            boxShadow: 4,
                                            '&:hover': { bgcolor: 'background.paper' },
                                          }}
                                        >
                                          {t('groupMessage:builder.editInDesignMode')}
                                        </Button>
                                      </span>
                                    </Tooltip>
                                  </Stack>
                                </Box>
                              </Box>
                            </Box>
                          )}

                          {effectivePreviewTab === 'edit' && (
                            <Box sx={{ pt: 2 }}>
                              <ControlledTextEditor name='body' noMargin disabled={isLocked} resizeAllowed rows={50} />
                            </Box>
                          )}
                        </ContentSection>
                      </Stack>
                    )}
                  </Grid>
                  {!isDeleted && (
                    <Grid item xs={12} textAlign='right' sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      {actionButtons}
                    </Grid>
                  )}
                </GridWrapper>
              </Paper>
            </Grid>
          </GridWrapper>
        </form>
      </FormProvider>
    </FormLoaderWrapper>
  );
};

export default GroupMessageEmailEditPage;
